import type {
  EditorChangeContent,
  EditorMessage,
  EditorMessageData,
  EditorMessageTypeButton,
  EditorMessageTypeChange,
  EditorMessageTypeMotion,
  EditorMode,
  EditorMotion,
} from '@/lib/editor/editor'
import {
  findChangeIndex,
  isNotChange,
  isTargetTextEditable,
} from '@/lib/editor/utils'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { usePreviewStore } from './use-preview-store'
import { useProjectStore } from './use-project-store'
import { removeAfterfix } from '@/lib/utils'

type EditorState = {
  mode: EditorMode | null
  state: 'on' | 'off'
  target: EditorMessageData | null
  waiting: boolean // ture if user just turn on editor
  loading: boolean // ture when saving changes to server
  reloading: boolean
  selecting: boolean
  changes: EditorChangeContent[]
  messages: EditorMessage[]
  historyStack: EditorChangeContent[][]
  redoStack: EditorChangeContent[][]
  lastSelectedTarget: EditorMessageData | null
  lastHistoryEntry: {
    type: string | null
    id: string | null
    child?: number | null
    timestamp: number | null
  }
  versionCheckDone: boolean
  versionCheckTimeoutId: NodeJS.Timeout | null
  reload: () => void
  turnOn: (mode: EditorMode) => void
  turnOff: (options?: { skipReload?: boolean }) => void
  navigate: (path: string) => void
  toggleEditor: (mode: EditorMode) => void
  selectParent: () => void
  removeTarget: () => void
  setWaiting: (waiting: boolean) => void
  setLoading: (loading: boolean) => void
  setSelecting: (selecting: boolean) => void
  message: (message: EditorMessage) => void
  process: (message: EditorChangeContent) => void
  preview: (type: EditorMessageTypeChange | EditorMessageTypeMotion) => void
  getLastChange: () => EditorChangeContent | null
  setTargetHref: (href: string) => void
  getIsParentLink: () => boolean
  getIsTargetIcon: () => boolean
  setTargetToNull: () => void
  getIsTargetNull: () => boolean
  getIsTargetLink: () => boolean
  getIsTargetText: () => boolean
  getIsTargetImage: () => boolean
  getIsTargetVideo: () => boolean
  getIsTargetAudio: () => boolean
  getIsTargetBlock: () => boolean
  getIsTargetButton: () => boolean
  getIsTargetForm: () => boolean
  getIsEditorOn: () => boolean
  getIsEditorOff: () => boolean
  getIsEditorModeEdit: () => boolean
  getIsEditorModeSelect: () => boolean
  getIsChangesEmpty: () => boolean
  getIsShowSaveButton: () => boolean
  getIsLastChangeRemove: () => boolean
  getIsTargetParentValid: () => boolean
  setTargetSrc: (src: string) => void
  setTargetSrcWithType: (src: string, type: 'image' | 'video' | 'audio') => void
  setTargetEmbed: (url: string) => void
  setTargetPayload: (payload: any) => void
  setTargetText: (text: string) => void
  setTargetStyle: (field: string, value: string) => void
  setTargetClick: (type: EditorMessageTypeButton, link: string) => void
  setTargetMotion: (motion: EditorMotion) => void
  undoLastRemove: () => void
  undo: () => void
  redo: () => void
  canUndo: () => boolean
  canRedo: () => boolean
  onEditorMessage: (message: EditorMessage) => void
}

export const useEditorStore = create<EditorState>()(
  devtools(
    immer((set, get) => {
      const replayChangesToIframe = () => {
        const { changes, message: sendMessage } = get()
        if (!changes.length) return
        changes.forEach((change) => {
          if (isNotChange(change)) return
          sendMessage(change as EditorMessage)
        })
      }

      const requestReloadAndReplay = () => {
        // Trigger iframe reload to reapply the current change set
        const iframe = document.getElementById(
          'project-preview',
        ) as HTMLIFrameElement | null
        if (iframe?.style) {
          iframe.style.transition = 'opacity 120ms ease'
          iframe.style.opacity = '0'
        }

        set((state) => {
          state.reloading = true
        })
        get().message({ type: 'editor.reload', data: null })

        // Fallback: if iframe never reports loaded, still attempt to replay
        setTimeout(() => {
          const { reloading } = get()
          if (!reloading) return
          replayChangesToIframe()
          set((state) => {
            state.reloading = false
          })
        }, 800)
      }

      return {
        mode: null,
        state: 'off',
        target: null,
        waiting: false,
        loading: false,
        reloading: false,
        selecting: false,
        changes: [],
        messages: [],
        historyStack: [],
        redoStack: [],
        lastSelectedTarget: null,
        lastHistoryEntry: {
          type: null,
          id: null,
          child: null,
          timestamp: null,
        },
        versionCheckDone: false,
        versionCheckTimeoutId: null,
        setWaiting: (waiting) => set(() => ({ waiting })),
        setLoading: (loading) => set(() => ({ loading })),
        setSelecting: (selecting) => set(() => ({ selecting })),
        getIsTargetIcon: () => get().target?.type === 'icon',
        setTargetToNull: () => set(() => ({ target: null })),
        getIsTargetNull: () => get().target === null,
        getIsParentLink: () => {
          const target = get().target
          if (!target || !target.parent) return false
          return (
            Boolean(target.parent.href) &&
            ['a', 'motion.a'].includes(target.parent.tag)
          )
        },
        getIsTargetLink: () => {
          const target = get().target
          if (!target) return false
          return (
            Boolean(target.href.old) &&
            ['a', 'motion.a', 'Link'].includes(target.component?.name ?? '')
          )
        },
        getIsTargetText: () => get().target?.type === 'text',
        getIsTargetImage: () => get().target?.type === 'image',
        getIsTargetVideo: () => get().target?.type === 'video',
        getIsTargetAudio: () => get().target?.type === 'audio',
        getIsTargetBlock: () => get().target?.type === 'block',
        getIsTargetButton: () => {
          const target = get().target
          if (!target) return false
          return (
            target.tag === 'button' ||
            target.component?.name === 'motion.button'
          )
        },
        getIsTargetForm: () => {
          const target = get().target
          return target?.component?.name === 'form' || target?.tag === 'form'
        },
        getIsEditorOn: () => get().state === 'on',
        getIsEditorOff: () => get().state === 'off',
        getIsEditorModeEdit: () => get().mode === 'edit',
        getIsEditorModeSelect: () => get().mode === 'select',
        getIsChangesEmpty: () => get().changes.length === 0,
        getIsShowSaveButton: () =>
          get().messages.some((message) => message.type === 'editor.select'),
        getIsTargetParentValid: () => {
          const target = get().target
          if (!target || !target.parent) return false
          return (
            target.parent.id !== 'app' &&
            !['html', 'body'].includes(target.parent.tag)
          )
        },
        setTargetHref: (href: string) => {
          set((state) => {
            if (!state.target) return
            state.target = {
              ...state.target,
              href: {
                old: state.target.href?.old ?? '',
                new: href,
              },
            }
          })
          get().preview('editor.change.href')
        },
        getLastChange: () => {
          const changes = get().changes
          return changes[changes.length - 1]
        },
        getIsLastChangeRemove: () => {
          const changes = get().changes
          return changes[changes.length - 1]?.type === 'editor.change.remove'
        },
        toggleEditor: (mode: EditorMode) => {
          const { state, turnOn, turnOff } = get()
          state === 'off' ? turnOn(mode) : turnOff()
        },
        selectParent: () => {
          const { target, message, getIsTargetParentValid } = get()
          if (getIsTargetParentValid()) {
            message({
              type: 'editor.select.parent',
              data: target as EditorMessageData,
            })
          }
        },
        removeTarget: () => {
          get().preview('editor.change.remove')
          set(() => ({ target: null })) // always put this line at last
        },
        reload: () => {
          get().message({ type: 'editor.reload', data: null })
          set(() => ({
            target: null,
            changes: [],
            messages: [],
            historyStack: [],
            redoStack: [],
            lastSelectedTarget: null,
            lastHistoryEntry: {
              type: null,
              id: null,
              child: null,
              timestamp: null,
            },
            reloading: true,
          }))
        },
        navigate: (path: string) => {
          get().message({ type: 'editor.navigate', data: path })
          set(() => ({
            target: null,
            changes: [],
            messages: [],
            historyStack: [],
            redoStack: [],
            lastSelectedTarget: null,
            lastHistoryEntry: {
              type: null,
              id: null,
              child: null,
              timestamp: null,
            },
          }))
        },
        turnOn: (mode: EditorMode) => {
          set(() => ({
            mode,
            state: 'on',
            target: null,
            waiting: true,
            selecting: false,
            changes: [],
            messages: [],
            historyStack: [],
            redoStack: [],
            lastSelectedTarget: null,
            lastHistoryEntry: {
              type: null,
              id: null,
              child: null,
              timestamp: null,
            },
          }))
          console.log('==turnOn===')
          get().message({ type: 'editor', data: { state: 'on', mode } })
          usePreviewStore.getState().setShowPreview(true)
          useProjectStore.getState().setIsToolboxOpen(false)
          // Close super prompts panel when editor turns on
          if (typeof window !== 'undefined') {
            // Import dynamically to avoid circular dependencies
            import('@/hooks/use-super-prompts-store').then(
              ({ useSuperPromptsStore }) => {
                const { closeSuperPromptsPanel } =
                  useSuperPromptsStore.getState()
                closeSuperPromptsPanel()
              },
            )
          }
        },
        turnOff: (options) => {
          console.log('🟢 [EDITOR] turnOff called with options:', options)
          set(() => ({
            mode: null,
            state: 'off',
            target: null,
            waiting: false,
            selecting: false,
            changes: [],
            messages: [],
            historyStack: [],
            redoStack: [],
            lastSelectedTarget: null,
            lastHistoryEntry: {
              type: null,
              id: null,
              child: null,
              timestamp: null,
            },
          }))
          console.log('🟢 [EDITOR] Sending message to iframe:', {
            type: 'editor',
            data: { state: 'off', mode: null, skipReload: options?.skipReload },
          })
          get().message({
            type: 'editor',
            data: { state: 'off', mode: null, skipReload: options?.skipReload },
          })
        },
        message: (message: EditorMessage) => {
          const iframe = document.getElementById(
            'project-preview',
          ) as HTMLIFrameElement
          if (iframe) {
            iframe.contentWindow?.postMessage(message, '*')
          }
          set((state) => {
            state.messages.push(message)
          })
        },
        process: (message: EditorChangeContent) => {
          if (isNotChange(message)) return
          const changes = get().changes
          const current = findChangeIndex(changes, message)
          const now = Date.now()

          set(
            (state) => {
              const isTextChange = message.type === 'editor.change.text'
              const last = state.lastHistoryEntry
              const sameTarget =
                isTextChange &&
                last.type === message.type &&
                last.id === message.data?.id &&
                last.child === message.data?.child
              const withinWindow =
                sameTarget && last.timestamp && now - last.timestamp < 600

              const shouldPushHistory = !(isTextChange && withinWindow)

              if (shouldPushHistory) {
                const MAX_HISTORY_SIZE = 50
                if (state.historyStack.length >= MAX_HISTORY_SIZE) {
                  state.historyStack = state.historyStack.slice(1)
                }
                state.historyStack.push([...state.changes])
                state.redoStack = []
              }

              if (current === -1) {
                state.changes.push(message)
              } else {
                state.changes[current] = message
              }

              state.lastHistoryEntry = {
                type: message.type,
                id: message.data?.id ?? null,
                child: (message.data as any)?.child ?? null,
                timestamp: now,
              }
            },
            false,
            'process',
          )
        },
        preview: (type: EditorMessageTypeChange | EditorMessageTypeMotion) => {
          const messageType = removeAfterfix(
            removeAfterfix(type as string, '__desktop'),
            '__mobile',
          ) as EditorMessageTypeChange | EditorMessageTypeMotion

          const { target, message, process } = get()
          const data = target as EditorMessageData
          message({ type: messageType, data })
          process({ type, data })
        },
        setTargetSrc: (src: string) => {
          set(
            (state) => {
              if (!state.target) return
              state.target = {
                ...state.target,
                src: {
                  old: state.target.src?.old ?? '',
                  new: src,
                },
              }
            },
            false,
            'setTargetSrc',
          )
          get().preview('editor.change.src')
        },
        /**
         * Sets the target source and type, allowing media type switching (e.g., image to video)
         * @param src - The new source URL
         * @param type - The new media type ('image', 'video', or 'audio')
         */
        setTargetSrcWithType: (
          src: string,
          type: 'image' | 'video' | 'audio',
        ) => {
          set(
            (state) => {
              if (!state.target) return
              state.target = {
                ...state.target,
                type,
                src: {
                  old: state.target.src?.old ?? '',
                  new: src,
                },
              }
            },
            false,
            'setTargetSrcWithType',
          )
          get().preview('editor.change.src')
        },
        setTargetEmbed: (url: string) => {
          set(
            (state) => {
              if (!state.target) return
              state.target = {
                ...state.target,
                embedUrl: url,
              }
            },
            false,
            'setTargetEmbed',
          )
          get().preview('editor.change.embedUrl')
        },

        setTargetPayload: (payload: Record<string, any>) => {
          set(
            (state) => {
              if (!state.target) return
              state.target = {
                ...state.target,
                payload: {
                  old: state.target.payload?.old ?? '',
                  new: payload,
                },
              }
            },
            false,
            'setTargetPayload',
          )
          get().preview('editor.change.payload')
        },

        setTargetText: (text: string) => {
          set((state) => {
            if (!state.target) return
            state.target = {
              ...state.target,
              text: {
                old: state.target.text?.old ?? '',
                new: text,
              },
            }
          })
          get().preview('editor.change.text')
        },
        setTargetStyle: (field: string, value: string) => {
          // 兼容 fontSize 隔离 mobile/desktop 变更
          const fieldKey = field.split('__')[0]

          set((state) => {
            if (!state.target || !state.target.style) return
            state.target = {
              ...state.target,
              style: {
                ...state.target.style,
                [fieldKey]: value,
              },
            }
          })
          get().preview(
            `editor.change.style.${field}` as EditorMessageTypeChange,
          )
        },
        setTargetClick: (type: EditorMessageTypeButton, link: string) => {
          set((state) => {
            if (!state.target) return
            state.target = {
              ...state.target,
              link,
            }
          })
          get().process({
            type,
            data: get().target as EditorMessageData,
          })
        },
        setTargetMotion: (motion: EditorMotion) => {
          set(
            (state) => {
              if (!state.target || !motion.name) return
              state.target = {
                ...state.target,
                motion: {
                  ...state.target.motion,
                  ...motion,
                },
              }
            },
            false,
            'setTargetMotion',
          )
          get().preview(
            `editor.change.motion.${motion.name}` as EditorMessageTypeMotion,
          )
        },
        undoLastRemove: () => {
          const { message, getIsLastChangeRemove, getLastChange } = get()
          if (!getIsLastChangeRemove()) return
          const last = getLastChange()
          set((state) => {
            state.target = last?.data as EditorMessageData
            state.lastSelectedTarget = last?.data as EditorMessageData
            state.changes = state.changes.slice(0, -1)
          })
          message({
            type: 'editor.change.remove.undo' as EditorMessageTypeChange,
            data: last?.data as EditorMessageData,
          })
        },
        /**
         * Check if undo is available
         * @returns true if there are changes in history stack
         */
        canUndo: () => {
          return get().historyStack.length > 0
        },
        /**
         * Check if redo is available
         * @returns true if there are changes in redo stack
         */
        canRedo: () => {
          return get().redoStack.length > 0
        },
        /**
         * Undo the last change
         * Moves current state to redo stack and restores previous state from history
         */
        undo: () => {
          const { historyStack, changes } = get()
          if (historyStack.length === 0) return

          // Save current state to redo stack
          const currentState = [...changes]
          const previousState = historyStack[historyStack.length - 1]
          const nextTarget =
            previousState[previousState.length - 1]?.data ?? null

          set((state) => {
            state.redoStack.push(currentState)
            state.historyStack = state.historyStack.slice(0, -1)
            state.changes = previousState
            state.reloading = true
            state.target =
              (nextTarget as EditorMessageData | null) ??
              state.lastSelectedTarget ??
              null
            state.lastHistoryEntry = {
              type: null,
              id: null,
              child: null,
              timestamp: null,
            }
          })

          // Send message to iframe to reload with previous state
          requestReloadAndReplay()
        },
        /**
         * Redo the last undone change
         * Moves next state from redo stack to current and adds current to history
         */
        redo: () => {
          const { redoStack, changes } = get()
          if (redoStack.length === 0) return

          // Get next state from redo stack
          const nextState = redoStack[redoStack.length - 1]
          const currentState = [...changes]
          const nextTarget = nextState[nextState.length - 1]?.data ?? null

          set((state) => {
            state.historyStack.push(currentState)
            state.redoStack = state.redoStack.slice(0, -1)
            state.changes = nextState
            state.reloading = true
            state.target =
              (nextTarget as EditorMessageData | null) ??
              state.lastSelectedTarget ??
              null
            state.lastHistoryEntry = {
              type: null,
              id: null,
              child: null,
              timestamp: null,
            }
          })

          // Send message to iframe to reload with next state
          requestReloadAndReplay()
        },
        onEditorMessage: (message: EditorMessage) => {
          const { type, data } = message
          if (!type || !type?.startsWith('editor')) return
          const { selecting, setTargetStyle } = get()
          const { project } = useProjectStore.getState()
          if (type === 'editor.change.style.height') {
            setTargetStyle('height', data?.style?.height ?? '')
          } else if (type === 'editor.change.style.display') {
            setTargetStyle('display', data?.style?.display ?? '')
          } else if (type === 'editor.change.style.paddingY' && data?.style) {
            setTargetStyle('paddingTop', data.style.paddingTop)
            setTargetStyle('paddingBottom', data.style.paddingBottom)
          } else if (type === 'editor.loaded') {
            const { reloading } = get()
            if (reloading) {
              replayChangesToIframe()
            }

            set(() => ({ reloading: false, versionCheckDone: false }))

            const iframe = document.getElementById(
              'project-preview',
            ) as HTMLIFrameElement | null
            if (iframe?.style) {
              iframe.style.opacity = '1'
            }

            // Update preview stage to 'running' when iframe finishes loading
            const previewStage = usePreviewStore.getState().stage
            if (previewStage === 'starting') {
              usePreviewStore.getState().setStage('running')
            }

            // Only check version if preview stage is 'running'
            const updatedPreviewStage = usePreviewStore.getState().stage
            const aiStage = useProjectStore.getState().aiStage
            if (updatedPreviewStage !== 'running' || aiStage !== 'idle') {
              console.log(
                `🟢 [EDITOR] Skipping version check - preview stage: ${updatedPreviewStage} - ai stage: ${aiStage}`,
              )
              return
            }

            // Ensure iframe re-enters editor mode after reload before replaying changes
            const iframeMode = get().mode
            if (iframeMode) {
              get().message({
                type: 'editor',
                data: { state: 'on', mode: iframeMode, skipReload: true },
              })
            }

            const { lastSelectedTarget } = get()
            if (lastSelectedTarget) {
              get().message({
                type: 'editor.select.restore',
                data: lastSelectedTarget,
              })
            }

            // Fallback: If no version message is received within 6 seconds, assume old version
            // But skip version check if this is a user-initiated refresh
            const previewState = usePreviewStore.getState()
            if (previewState.skipVersionCheck) {
              console.log(
                '🟢 [EDITOR] Editor loaded - skipping version check (user-initiated refresh)',
              )
              // Reset the flag for future checks
              previewState.setSkipVersionCheck(false)
              set(() => ({ versionCheckDone: true }))
            } else {
              console.log(
                '🟢 [EDITOR] Editor loaded, starting version check timeout...',
              )
              const timeoutId = setTimeout(() => {
                const { versionCheckDone } = get()
                if (!versionCheckDone) {
                  console.log(
                    '🟢 [EDITOR] Version check timeout - no version message received, assuming old version',
                  )
                  useProjectStore.getState().checkEditorVersion('1.0.0')
                  set(() => ({ versionCheckDone: true }))
                } else {
                  console.log(
                    '🟢 [EDITOR] Version check timeout - already received version, skipping',
                  )
                }
              }, 6_000)

              set(() => ({ versionCheckTimeoutId: timeoutId }))
            }
          } else if (type === 'editor.version') {
            // Only check version if preview stage is 'running'
            const previewStore = usePreviewStore.getState()
            if (previewStore.stage !== 'running') {
              console.log(
                '🟢 [EDITOR] Skipping version check - preview stage:',
                previewStore.stage,
              )
              return
            }

            // Skip version check if this is a user-initiated refresh
            if (previewStore.skipVersionCheck) {
              console.log(
                '🟢 [EDITOR] Skipping version check (user-initiated refresh)',
              )
              // Clear the timeout and reset the flag
              const { versionCheckTimeoutId } = get()
              if (versionCheckTimeoutId) {
                clearTimeout(versionCheckTimeoutId)
              }
              previewStore.setSkipVersionCheck(false)
              set(() => ({
                versionCheckDone: true,
                versionCheckTimeoutId: null,
              }))
              return
            }

            // Handle version from iframe - this is the primary version check method
            const iframeVersion = data as string
            console.log(
              '🟢 [EDITOR] Received editor version message from iframe:',
              iframeVersion,
            )

            // Clear the timeout since we got the version
            const { versionCheckTimeoutId } = get()
            if (versionCheckTimeoutId) {
              console.log('🟢 [EDITOR] Clearing version check timeout')
              clearTimeout(versionCheckTimeoutId)
            }

            // Check version and trigger rebuild if needed
            set(() => ({ versionCheckDone: true, versionCheckTimeoutId: null }))
            useProjectStore.getState().checkEditorVersion(iframeVersion)
          } else if (type === 'editor.scroll.select.target' && selecting) {
            set((state) => {
              if (state.target) {
                state.target = {
                  ...state.target,
                  link: data as EditorMessageData,
                }
              }
            })
            const { target, process } = get()
            process({
              type: 'editor.change.button.scroll',
              data: target as EditorMessageData,
            })
          } else if (['editor.select', 'editor.change.text'].includes(type)) {
            set(
              (state) => {
                state.target = data as EditorMessageData
                state.lastSelectedTarget = data as EditorMessageData
              },
              false,
              'editor.select-or-editor.change-text',
            )
            if (type === 'editor.change.text') {
              get().process(message as EditorChangeContent)
            } else if (type === 'editor.select') {
              set(
                (state) => {
                  state.messages.push(message)
                },
                false,
                'editor-message',
              )
              usePreviewStore.getState().setShowPreview(false)
              if (data?.type === 'text') {
                get().message({
                  type: 'editor.select.editable',
                  data: {
                    ...data,
                    editable: isTargetTextEditable(
                      project?.file_system_tree!,
                      data!,
                    ),
                  },
                })
              }
            }
          }
        },
      }
    }),
  ),
)
